import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { DeleteIconComponent } from '@itero/ui-toolkit-angular/icons';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { IntuitiveToothForTreatment, type DiagramToothAndBridgeListViewItem } from '@modules/rx-intuitive-teeth-diagram/models';
import { TOOTH_EDITOR_TOOTH_NUMBER_SEPARATOR } from '@modules/rx-intuitive-teeth-diagram/models/consts';
import { IntuitiveIndication } from '@modules/rx-intuitive-teeth-diagram/models/intuitive-indication';
import { RxIntuitiveTeethDiagramBridgeFacade } from '@modules/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram-bridge.facade';
import { RxIntuitiveIndicationRulesService } from '@modules/rx-intuitive-teeth-diagram/services/rx-intuitive-indication-rules.service';
import { formatBridgeSpanDisplayFromResolvedNumbers } from '@modules/rx-intuitive-teeth-diagram/utils/format-bridge-span-display';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { IdName } from '@shared/models/id-name';
import { PonticDesignEnum } from '@shared/models/pontic-design.enum';
import { takeUntil } from 'rxjs';
import { RxIntuitiveIndicationBadgeComponent } from '../rx-intuitive-indication-badge/rx-intuitive-indication-badge.component';
import { RxIntuitiveToothEditorBodyComponent } from '../rx-intuitive-tooth-editor-body/rx-intuitive-tooth-editor-body.component';

@Component({
	selector: 'rx-intuitive-bridge-editor',
	templateUrl: './rx-intuitive-bridge-editor.component.html',
	styleUrls: ['../rx-intuitive-tooth-editor/rx-intuitive-tooth-editor.component.scss', './rx-intuitive-bridge-editor.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		TlkButtonComponent,
		RxIntuitiveIndicationBadgeComponent,
		DeleteIconComponent,
		RxIntuitiveToothEditorBodyComponent
	]
})
export class RxIntuitiveBridgeEditorComponent extends BaseDestroyableComponent {
	@Input() teeth: IntuitiveToothForTreatment[] = [];
	@Input() hasUnsavedChanges = false;
	@Input() canEdit = false;
	@Input() canDelete = false;
	@Input() bridgeTeethFlat: Tooth[] = [];
	@Input() ponticDesigns: IdName<PonticDesignEnum>[] = [];
	@Input() bridgeListItem: DiagramToothAndBridgeListViewItem | null = null;

	@Output() delete = new EventEmitter<void>();
	@Output() crownFormChanged = new EventEmitter<CrownForm>();
	@Output() additionalInfoApplied = new EventEmitter<{ toothIds: number[]; result: AdditionalInfoResult }>();
	@Output() clearDisabledFields = new EventEmitter<{ toothIds: number[]; payload: Partial<Record<string, unknown>> }>();
	@Output() ponticDesignChanged = new EventEmitter<{ toothIds: number[]; ponticDesignId: PonticDesignEnum | null }>();

	readonly badgeIndicationId = IntuitiveIndication.Bridge;

	constructor(
		private readonly indicationsService: RxIntuitiveIndicationRulesService,
		private readonly teethDiagramBridgeFacade: RxIntuitiveTeethDiagramBridgeFacade
	) {
		super();
	}

	get indicationId(): IntuitiveIndication {
		return this.teeth[0]?.indicationId ?? this.indicationsService.getDefaultIndication();
	}

	get hasAssignedIndication(): boolean {
		return this.teeth.length > 0 && !this.indicationsService.isDefaultIndication(this.teeth[0]?.indicationId);
	}

	get isIndicationVisible(): boolean {
		return this.hasAssignedIndication;
	}

	get isReadOnly(): boolean {
		return !this.canEdit || !this.hasAssignedIndication;
	}

	get toothNumbersLabel(): string {
		return this.teeth.map(t => t.toothNumberInSystem).join(TOOTH_EDITOR_TOOTH_NUMBER_SEPARATOR);
	}

	get bridgeSpanLabel(): string {
		if (!this.bridgeListItem) {
			return '';
		}

		return formatBridgeSpanDisplayFromResolvedNumbers(
			this.bridgeListItem.startToothNumberInSystem,
			this.bridgeListItem.endToothNumberInSystem ?? this.bridgeListItem.startToothNumberInSystem
		);
	}

	get shouldHideProperties(): boolean {
		return (
			!this.teeth[0] ||
			!this.indicationsService.shouldShowToothEditorProperties({
				indicationId: this.teeth[0].indicationId
			})
		);
	}

	signalDeleteBridge(): void {
		this.delete.emit();
	}

	onBodyCrownFormChanged(crownForm: CrownForm): void {
		this.crownFormChanged.emit(crownForm);
	}

	onBodyAdditionalInfoApplied(event: { toothIds: number[]; result: AdditionalInfoResult }): void {
		this.additionalInfoApplied.emit(event);
	}

	onBodyClearDisabledFields(event: { toothIds: number[]; payload: Partial<Record<string, unknown>> }): void {
		this.clearDisabledFields.emit(event);
	}

	onBodyPonticDesignChanged(event: { toothIds: number[]; ponticDesignId: PonticDesignEnum | null }): void {
		this.ponticDesignChanged.emit(event);
	}

	onTreatmentSummaryClick(): void {
		const indicationId = this.teeth[0]?.indicationId;

		if (indicationId === IntuitiveIndication.BridgeImplantBased) {
			const modalResult$ = this.teethDiagramBridgeFacade.openModalForUnitTypeIfNeeded(indicationId);

			if (modalResult$) {
				modalResult$.pipe(takeUntil(this.componentAlive$)).subscribe();
			}
		}
	}
}
